import { useMemo } from 'react';
import type { FC } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Line, Bar } from 'react-chartjs-2';
import { StatCard, ChartCard } from '../components/Cards';
import { groupMetrics } from '../utils/group-metrics';
import { baseChartOptions, stackedBarOptions, lineDataset, barDataset, CHART_COLORS } from '../utils/charts';
import type { OpsContext } from '../types';

const Engagement: FC = () => {
  const { current, history, grouping } = useOutletContext<OpsContext>();
  const buckets = useMemo(() => groupMetrics(history, grouping), [history, grouping]);
  const labels = buckets.map((b) => b.label);

  // Cumulative AI / imported counts aren't in DailyMetrics (only their
  // per-period deltas are), so the cumulative line shows the totalArticles
  // rollup. For AI vs imported splits, the per-period bars do the work.
  const cumulativeData = {
    labels,
    datasets: [
      lineDataset('Total articles', buckets.map((b) => b.totalArticles), CHART_COLORS.articlesTotal),
    ],
  };

  const perPeriodData = {
    labels,
    datasets: [
      barDataset('AI', buckets.map((b) => b.dailyAiArticles), CHART_COLORS.articlesAi, { stack: 'articles' }),
      barDataset('Imported', buckets.map((b) => b.dailyImportArticles), CHART_COLORS.articlesImport, { stack: 'articles' }),
    ],
  };

  // The aggregator stores total AI and total imported as per-period deltas
  // only, not cumulative on the snapshot. Sum them across the loaded range
  // for the headline stat cards so the figure has the loaded window as its
  // implicit denominator.
  const aiInRange = history.reduce((sum, m) => sum + m.dailyAiArticles, 0);
  const importedInRange = history.reduce((sum, m) => sum + m.dailyImportArticles, 0);

  return (
    <>
      <div className="row row-cols-2 row-cols-md-4 g-2 mb-3">
        <StatCard label="Total articles" value={(current?.totalArticles ?? 0).toString()} variant="primary" />
        <StatCard label="AI (in range)" value={aiInRange.toString()} variant="info" />
        <StatCard label="Imported (in range)" value={importedInRange.toString()} variant="success" />
        <StatCard label="Users w/ first article" value={(current?.usersWithFirstArticle ?? 0).toString()} variant="warning" />
      </div>

      {buckets.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <ChartCard title="Cumulative">
            <Line options={baseChartOptions} data={cumulativeData} />
          </ChartCard>
          <ChartCard title="Per period (stacked: AI + imported)">
            <Bar options={stackedBarOptions} data={perPeriodData} />
          </ChartCard>
        </>
      )}
    </>
  );
};

const EmptyState: FC = () => (
  <div className="text-center text-muted py-5">
    <p>No metrics data in the selected range. Metrics are computed daily at 03:00 UTC.</p>
  </div>
);

export default Engagement;
